import { useCallback, useEffect, useMemo, useState } from "react"

import api, { getEnterpriseId } from "../../../lib/api"
import { getStoredUser } from "../../../services/auth"
import { emitSnackbar } from "../../../lib/snackbar-emitter"
import { loader } from "../../../lib/loader"
import manageErrors from "../../../lib/error-management"

const RESTRICTED_WORDS_PATH = (enterpriseId) => `enterprises/${enterpriseId}/restricted_words/`

const normalizeCollection = (data) => {
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.results)) return data.results
  if (Array.isArray(data?.data)) return data.data
  return []
}

const parseApiItem = (item = {}) => {
  const words = Array.isArray(item.words)
    ? item.words.map((w) => (typeof w === "string" ? w : w?.word)).filter(Boolean)
    : []

  return {
    id: item.id,
    name: item.name || "",
    words: words.join(", "),
  }
}

const toApiPayload = ({ name, words, accountcode, scout }) => {
  const rawWords = Array.isArray(words) ? words.join(",") : String(words || "")

  return {
    name: name?.trim(),
    accountcode: accountcode || "",
    scout: scout ?? 1,
    queues: [],
    words: rawWords
      .split(",")
      .map((w) => w.trim())
      .filter(Boolean),
  }
}

export const useRestrictedWords = () => {
  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState([])
  const [editing, setEditing] = useState(null)
  const [deleting, setDeleting] = useState(null)

  const enterpriseId = getEnterpriseId()
  const user = getStoredUser()
  const accountcode =
    user?.profile?.enterprise?.accountcode ||
    user?.profile?.enterprise?.id ||
    user?.enterprise?.id ||
    ""

  const load = useCallback(async () => {
    if (!enterpriseId) return

    setLoading(true)
    loader.on()

    try {
      const response = await api.get(`${RESTRICTED_WORDS_PATH(enterpriseId)}?scout=1`)
      if (response.status === 200) {
        const list = normalizeCollection(response.data)
        setItems(list.map(parseApiItem))
      } else {
        emitSnackbar({ message: manageErrors(response.data), severity: "error" })
      }
    } catch (error) {
      emitSnackbar({ message: "Erro ao carregar as listas de palavras-chave.", severity: "error" })
    } finally {
      loader.off()
      setLoading(false)
    }
  }, [enterpriseId])

  useEffect(() => {
    void load()
  }, [load])

  const save = useCallback(
    async (payload) => {
      const isEdit = Boolean(payload?.id)

      try {
        loader.on()
        const payloadWithAccount = {
          ...payload,
          accountcode,
          scout: 1,
        }

        const response = isEdit
          ? await api.patch(`${RESTRICTED_WORDS_PATH(enterpriseId)}${payload.id}/?scout=1`, toApiPayload(payloadWithAccount))
          : await api.post(RESTRICTED_WORDS_PATH(enterpriseId), toApiPayload(payloadWithAccount))

        if (response.status === 200 || response.status === 201) {
          emitSnackbar({
            message: isEdit
              ? "Lista de palavras-chave atualizada com sucesso."
              : "Lista de palavras-chave inserida com sucesso.",
            severity: "success",
          })
          await load()
          return true
        }

        emitSnackbar({ message: manageErrors(response.data), severity: "error" })
        return false
      } catch (error) {
        const serverError = error?.response?.data || (error?.message && String(error.message)) || ''
        emitSnackbar({
          message: `Erro ao salvar a lista de palavras-chave.${serverError ? ` (${serverError})` : ""}`,
          severity: "error",
        })
        return false
      } finally {
        loader.off()
      }
    },
    [accountcode, enterpriseId, load],
  )

  const remove = useCallback(
    async (item) => {
      if (!item?.id) return false

      try {
        loader.on()
        const response = await api.delete(`${RESTRICTED_WORDS_PATH(enterpriseId)}${item.id}/?scout=1`)
        if (response.status === 204) {
          emitSnackbar({ message: "Lista excluída com sucesso.", severity: "success" })
          await load()
          return true
        }

        emitSnackbar({ message: manageErrors(response.data), severity: "error" })
        return false
      } catch (error) {
        emitSnackbar({ message: "Erro ao excluir a lista.", severity: "error" })
        return false
      } finally {
        loader.off()
      }
    },
    [enterpriseId, load],
  )

  const actions = useMemo(
    () => ({
      setEditing,
      setDeleting,
      setItems,
      setLoading,
    }),
    [],
  )

  return {
    items,
    loading,
    editing,
    deleting,
    setEditing,
    setDeleting,
    load,
    save,
    remove,
    actions,
  }
}
